package com.solevo.dist.network.implementation.firebase.api.customers.models

import com.solevo.dist.application.models.Company
import com.solevo.dist.application.models.CreditInfo
import com.solevo.dist.application.models.Customer
import com.solevo.dist.application.models.Loyalty
import com.solevo.dist.application.models.Rebate
import com.solevo.dist.application.models.RebateEntity
import com.solevo.dist.application.models.Rewards
import com.solevo.dist.application.models.TierName
import com.solevo.dist.network.implementation.firebase.utils.time.toKotlinInstant
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.serialization.Serializable

@Serializable
data class FirebaseCustomer(
    val customerId: String = "",
    val legalEntityId: String? = null,
    var currency: String = "",
    val isBlocked: Boolean? = false,
    val groupId: String? = "",
    var company: FirebaseCompany? = null,
    var creditInfo: FirebaseCreditInfo? = null,
    var rebate: FirebaseRebate? = null,
    var rewards: FirebaseRewards? = null,
    val loyalty: FirebaseLoyalty? = null,
    val hasOverdueBlockingRule: Boolean? = false,
    val overdueDays: Int? = 0,
    val minOpenInvoiceDueDate: Timestamp? = Timestamp.now(),
    val defaultWarehouseId: String? = null,
    val isManuallyImported: Boolean? = null,
    val gtcAccepted: Boolean? = null,
    val isGtcAccepted: String? = "",
    val loyaltyEnabled: Boolean? = null,
)

@Serializable
data class FirebaseCompany(
    val name: String = "",
    val country: String = "",
    val street: String = "",
    val streetNumber: String = "",
    val city: String = "",
    val zipcode: String = "",
    val tier: String = "",
)

@Serializable
data class FirebaseCreditInfo(
    val availableCredit: Double = 0.0,
    val openInvoices: Double = 0.0,
    val goodsInDelivery: Double = 0.0,
    val totalCreditLine: Double = 0.0,
    val availableCreditAfterOpenOrders: Double = 0.0,
)

@Serializable
data class FirebaseRebate(
    val accruedRebate: Double = 0.0,
    val rebates: List<FirebaseRebateEntity>? = null,
)

@Serializable
data class FirebaseRewards(
    val creditScore: Int = 0,
    val paymentTerms: Int = 0,
)

@Serializable
data class FirebaseRebateEntity(
    val name: String = "",
    val code: String = "",
    val value: Double? = null,
)

@Serializable
data class FirebaseLoyalty(
    val expirationDate: Timestamp? = Timestamp.now(),
    val loyaltyCardNumber : String? = "",
    val loyaltyProgramId : String? = "",
    val tierId : String? = "",
    val tierName : List<FirebaseTierName>? = null,
    val statusPoints : Int? = 0,
    val totalRewardPoints : Int? = 0,
)

@Serializable
data class FirebaseTierName (
    var description: String? = "",
    var languageId: String? = "",
    var name: String? = ""
)

fun FirebaseCustomer.toCustomer(): Customer {
    return Customer(
        customerId = customerId,
        legalEntityId = legalEntityId,
        currency = currency,
        isBlocked = isBlocked ?: false,
        groupId = groupId,
        company = company?.toCompany(),
        creditInfo = creditInfo?.toCreditInfo(),
        rebate = rebate?.toRebate(),
        rewards = rewards?.toRewards(),
        loyalty = loyalty?.toLoyalty(),
        hasOverdueBlockingRule = hasOverdueBlockingRule ?: false,
        overdueDays = overdueDays ?: 0,
        minOpenInvoiceDueDate = minOpenInvoiceDueDate?.toKotlinInstant(),
        defaultWarehouseId = defaultWarehouseId,
        isManuallyImported = isManuallyImported ?: false,
        gtcAccepted = gtcAccepted ?: false,
        isGtcAccepted = isGtcAccepted ?: "",
        loyaltyEnabled = loyaltyEnabled ?: false,
    )
}

fun FirebaseCompany.toCompany(): Company {
    return Company(
        name = name,
        country = country,
        street = street,
        streetNumber = streetNumber,
        city = city,
        zipcode = zipcode,
        tier = tier,
    )
}

fun FirebaseCreditInfo.toCreditInfo(): CreditInfo {
    return CreditInfo(
        availableCredit = availableCredit,
        openInvoices = openInvoices,
        goodsInDelivery = goodsInDelivery,
        totalCreditLine = totalCreditLine,
        availableCreditAfterOpenOrders = availableCreditAfterOpenOrders,
    )
}

fun FirebaseRebate.toRebate(): Rebate {
    return Rebate(
        accruedRebate = accruedRebate,
        rebates = rebates?.map { it.toRebateEntity() },
    )
}

fun FirebaseRebateEntity.toRebateEntity(): RebateEntity {
    return RebateEntity(
        name = name,
        code = code,
        value = value,
    )
}

fun FirebaseRewards.toRewards(): Rewards {
    return Rewards(
        creditScore = creditScore,
        paymentTerms = paymentTerms,
    )
}

fun FirebaseLoyalty.toLoyalty(): Loyalty {
    return Loyalty(
        expirationDate = expirationDate?.toKotlinInstant(),
        loyaltyCardNumber = loyaltyCardNumber,
        loyaltyProgramId = loyaltyProgramId,
        tierId = tierId,
        tierName = tierName?.map { it.toTierName() },
        statusPoints = statusPoints ?: 0,
        totalRewardPoints = totalRewardPoints ?: 0,
    )
}

fun FirebaseTierName.toTierName(): TierName {
    return TierName(
        description = description,
        languageId = languageId,
        name = name,
    )
}